import { Injectable } from '@angular/core';
import { Database, ref, push, set, update, remove, onValue, get, query, orderByChild, limitToLast, endBefore, startAt } from '@angular/fire/database';
import { Observable } from 'rxjs';

export interface PaginatedNewsResult {
  news: any[];
  hasMore: boolean;
  lastKey: string | null;
  totalCount?: number;
} 

@Injectable({
  providedIn: 'root'
})
export class NewsService {

  private basePath = 'news';
  private readonly ITEMS_PER_PAGE = 10;

  constructor(private db: Database) {}

  // ========== GET ALL (For backward compatibility) ==========
  getAllNews(): Observable<any[]> {
    return new Observable((observer) => {
      const newsRef = ref(this.db, this.basePath);
      onValue(newsRef, snapshot => {
        const data = snapshot.val();
        const result = data ? Object.keys(data).map(id => ({ id, ...data[id] })) : [];
        observer.next(result);
      });
    });
  }

  // ========== GET PAGINATED NEWS ==========
  getNewsPage(pageNumber: number = 1, lastTimestamp: number | null = null): Promise<PaginatedNewsResult> {
    return new Promise(async (resolve, reject) => {
      try {
        // Try indexed query first, fallback to loading all if index not available
        try {
          const result = await this.getNewsPageWithIndex(pageNumber, lastTimestamp);
          resolve(result);
        } catch (indexError: any) {
          // If index error, fallback to client-side pagination
          if (indexError.message?.includes('index') || indexError.code === 'PERMISSION_DENIED') {
            console.warn('Index not available, using client-side pagination');
            const result = await this.getNewsPageClientSide(pageNumber);
            resolve(result);
          } else {
            throw indexError;
          }
        }
      } catch (error) {
        console.error('Error in getNewsPage:', error);
        reject(error);
      }
    });
  }

  // ========== GET NEWS WITH INDEX (Requires Firebase index) ==========
  private async getNewsPageWithIndex(pageNumber: number, lastTimestamp: number | null): Promise<PaginatedNewsResult> {
    const newsRef = ref(this.db, this.basePath);
    let newsQuery;

    if (pageNumber === 1 || !lastTimestamp) {
      newsQuery = query(
        newsRef,
        orderByChild('createdAt'),
        limitToLast(this.ITEMS_PER_PAGE)
      );
    } else {
      newsQuery = query(
        newsRef,
        orderByChild('createdAt'),
        endBefore(lastTimestamp),
        limitToLast(this.ITEMS_PER_PAGE)
      );
    }

    const snapshot = await get(newsQuery);
    const data = snapshot.val();

    if (!data) {
      return {
        news: [],
        hasMore: false,
        lastKey: null
      };
    }

    const newsArray = Object.keys(data)
      .map(id => ({ id, ...data[id] }))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    const oldestItem = newsArray[newsArray.length - 1];
    const newLastTimestamp = oldestItem?.createdAt || null;
    const hasMore = newsArray.length === this.ITEMS_PER_PAGE;

    return {
      news: newsArray,
      hasMore,
      lastKey: newLastTimestamp
    };
  }

  // ========== GET NEWS CLIENT-SIDE PAGINATION (No index required) ==========
  private cachedAllNews: any[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 60000; // 1 minute cache

  private async getNewsPageClientSide(pageNumber: number, useCache: boolean = true): Promise<PaginatedNewsResult> {
    const now = Date.now();
    
    // Use cache if available and fresh
    if (useCache && this.cachedAllNews && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      return this.paginateFromCache(pageNumber);
    }

    // Load all news (only once, then cache)
    const newsRef = ref(this.db, this.basePath);
    const snapshot = await get(newsRef);
    const data = snapshot.val();

    if (!data) {
      return {
        news: [],
        hasMore: false,
        lastKey: null
      };
    }

    // Convert to array and sort by createdAt descending (newest first)
    const allNews = Object.keys(data)
      .map(id => ({ id, ...data[id] }))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    // Cache the results
    this.cachedAllNews = allNews;
    this.cacheTimestamp = now;

    return this.paginateFromCache(pageNumber);
  }

  private paginateFromCache(pageNumber: number): PaginatedNewsResult {
    if (!this.cachedAllNews) {
      return {
        news: [],
        hasMore: false,
        lastKey: null
      };
    }

    // Calculate pagination
    const startIndex = (pageNumber - 1) * this.ITEMS_PER_PAGE;
    const endIndex = startIndex + this.ITEMS_PER_PAGE;
    const paginatedNews = this.cachedAllNews.slice(startIndex, endIndex);

    // Get the oldest item's timestamp for consistency
    const oldestItem = paginatedNews[paginatedNews.length - 1];
    const lastKey = oldestItem?.createdAt || null;
    const hasMore = endIndex < this.cachedAllNews.length;

    return {
      news: paginatedNews,
      hasMore,
      lastKey
    };
  }

  // Clear cache when news is added/updated/deleted
  clearCache(): void {
    this.cachedAllNews = null;
    this.cacheTimestamp = 0;
  }

  // Get cached news for categories (fast)
  getCachedNewsForCategories(): any[] {
    return this.cachedAllNews || [];
  }

  // ========== GET TOTAL COUNT ==========
  getTotalNewsCount(): Promise<number> {
    return new Promise(async (resolve, reject) => {
      try {
        const newsRef = ref(this.db, this.basePath);
        const snapshot = await get(newsRef);
        const data = snapshot.val();
        const count = data ? Object.keys(data).length : 0;
        resolve(count);
      } catch (error) {
        console.error('Error getting total count:', error);
        // Return 0 on error to prevent breaking the UI
        resolve(0);
      }
    });
  }

  // ========== GET CATEGORIES ONLY (Fast - no full data load) ==========
  getCategoriesOnly(): Promise<string[]> {
    return new Promise(async (resolve) => {
      try {
        // If we have cached data, use it
        if (this.cachedAllNews) {
          const categories = this.cachedAllNews
            .map(item => item.category)
            .filter(cat => cat && cat.trim() !== '');
          resolve([...new Set(categories)].sort());
          return;
        }

        // Otherwise, load minimal data (just categories)
        const newsRef = ref(this.db, this.basePath);
        const snapshot = await get(newsRef);
        const data = snapshot.val();
        
        if (!data) {
          resolve([]);
          return;
        }

        // Extract only categories (much faster than loading full objects)
        const categories = Object.keys(data)
          .map(id => data[id]?.category)
          .filter(cat => cat && cat.trim() !== '');

        resolve([...new Set(categories)].sort());
      } catch (error) {
        console.error('Error getting categories:', error);
        resolve([]);
      }
    });
  }

  // ========== GET CATEGORY COUNT (Fast) ==========
  getCategoryCount(category: string): Promise<number> {
    return new Promise(async (resolve) => {
      try {
        // If cached, use cache
        if (this.cachedAllNews) {
          const count = this.cachedAllNews.filter(item => item.category === category).length;
          resolve(count);
          return;
        }

        // Otherwise, count from database
        const newsRef = ref(this.db, this.basePath);
        const snapshot = await get(newsRef);
        const data = snapshot.val();
        
        if (!data) {
          resolve(0);
          return;
        }

        const count = Object.keys(data)
          .filter(id => data[id]?.category === category)
          .length;

        resolve(count);
      } catch (error) {
        console.error('Error getting category count:', error);
        resolve(0);
      }
    });
  }

  // ========== GET ONE BY ID ==========
  getNewsById(id: string): Observable<any> {
    return new Observable((observer) => {
      const itemRef = ref(this.db, `${this.basePath}/${id}`);
      onValue(itemRef, snapshot => {
        observer.next({ id, ...snapshot.val() });
      });
    });
  }

  // ========== ADD (SAVE ID INSIDE DATA) ==========
  addNews(news: any) {
    this.clearCache(); // Clear cache when adding
    const listRef = ref(this.db, this.basePath);
    const newRef = push(listRef); // generates unique ID
    const id = newRef.key; // get ID

    const newsWithId = { id, ...news }; // add ID to object

    return set(newRef, newsWithId); // save to DB with ID included
  }

  // ========== UPDATE ==========
  updateNews(id: string, news: any) {
    this.clearCache(); // Clear cache when updating
    const itemRef = ref(this.db, `${this.basePath}/${id}`);
    return update(itemRef, { id, ...news }); // make sure ID stays in structure
  }

  // ========== DELETE ==========
  deleteNews(id: string) {
    this.clearCache(); // Clear cache when deleting
    const itemRef = ref(this.db, `${this.basePath}/${id}`);
    return remove(itemRef);
  }
}
